import { apiClient } from '../utils/apiClient';

/**
 * Tipos para los datos filtrados de RE-PRO-VE
 * Estos endpoints SOLO retornan datos que el usuario tiene permiso de ver
 */

export interface ReproveRepresentative {
  id: string;
  name: string;
  employeeNumber: string;
  zone: {
    id: string;
    name: string;
  };
}

export interface ReproveCity {
  id: string;
  name: string;
  zoneId: string;
  zoneName: string;
}

export interface ReproveChain {
  id: string;
  name: string;
  zoneId: string;
}

export interface ReproveSeller {
  id: string;
  name: string;
  active?: boolean;
}

export interface ReproveStore {
  id: string;
  name: string;
  chainId: string;
  chainName: string;
  cityId: string;
  cityName: string;
  sellers: ReproveSeller[];
}
export interface ReproveStoresResponse {
  stores: ReproveStore[];
  availableSellers: ReproveSeller[];
}
/**
 * Servicio para interactuar con los endpoints específicos de RE-PRO-VE
 * Estos endpoints implementan filtros por rol y permisos
 */
class ReproveDataService {
  /**
   * Obtiene los representantes según el rol del usuario:
   * - ZONE_MANAGER: Solo representantes de su(s) zona(s)
   * - GENERAL_MANAGER/ADMIN: Todos los representantes
   */
  async getRepresentatives(): Promise<ReproveRepresentative[]> {
    try {
      return await apiClient.get<ReproveRepresentative[]>(
        '/reprove/representatives'
      );
    } catch (error) {
      console.error('Error loading representatives:', error);
      throw new Error('No se pudieron cargar los representantes');
    }
  }

  /**
   * Obtiene las ciudades donde un representante específico tiene tiendas asignadas
   * IMPORTANTE: Solo retorna ciudades donde el representante REALMENTE tiene tiendas
   */
  async getCitiesForRepresentative(
    representativeId: string
  ): Promise<ReproveCity[]> {
    if (!representativeId) {
      return [];
    }

    try {
      return await apiClient.get<ReproveCity[]>(
        `/reprove/cities/${representativeId}`
      );
    } catch (error) {
      console.error(
        `Error loading cities for representative ${representativeId}:`,
        error
      );
      throw new Error('No se pudieron cargar las ciudades del representante');
    }
  }

  /**
   * Obtiene las cadenas donde un representante tiene tiendas en una ciudad específica
   */
  async getChainsForRepresentativeInCity(
    representativeId: string,
    cityId: string
  ): Promise<ReproveChain[]> {
    if (!representativeId || !cityId) {
      return [];
    }

    try {
      return await apiClient.get<ReproveChain[]>(
        `/reprove/chains/${representativeId}/${cityId}`
      );
    } catch (error) {
      console.error(
        `Error loading chains for rep ${representativeId} in city ${cityId}:`,
        error
      );
      throw new Error(
        'No se pudieron cargar las cadenas del representante en esta ciudad'
      );
    }
  }
  /**
   * Obtiene los sellers de un representante en una ciudad
   * (independiente de la cadena)
   */
  async getSellersForRepresentativeInCity(
    representativeId: string,
    cityId: string
  ): Promise<ReproveSeller[]> {
    if (!representativeId || !cityId) {
      return [];
    }

    try {
      return await apiClient.get<ReproveSeller[]>(
        `/reprove/sellers/${representativeId}/${cityId}`
      );
    } catch (error) {
      console.error(
        `Error loading sellers for rep ${representativeId} in city ${cityId}:`,
        error
      );
      return [];
    }
  }

  /**
   * Obtiene las tiendas de un representante en una ciudad específica
   * Opcionalmente filtra por cadena
   * Incluye las vendedoras disponibles (excluyendo al representante mismo)
   */
  async getStoresForRepresentativeInCity(
    representativeId: string,
    cityId: string,
    chainId?: string
  ): Promise<ReproveStoresResponse> {
    if (!representativeId || !cityId) {
      return { stores: [], availableSellers: [] };
    }

    try {
      const url = `/reprove/stores/${representativeId}/${cityId}${
        chainId ? `?chainId=${chainId}` : ''
      }`;
      // return await apiClient.get<ReproveStore[]>(url);
      return await apiClient.get<ReproveStoresResponse>(url);
    } catch (error) {
      console.error(
        `Error loading stores for rep ${representativeId} in city ${cityId}:`,
        error
      );
      throw new Error(
        'No se pudieron cargar las tiendas del representante en esta ciudad'
      );
    }
  }
}

export const reproveDataService = new ReproveDataService();
